import React, { useState, useRef } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Person,
  Chat,
  NewReleases,
  Clear,
  DoneAll,
  SwapHoriz,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNotifications } from '../contexts/NotificationContext';

type NotificationType = 'LEAD_ASSIGNED' | 'LEAD_TRANSFERRED' | 'CONVERSATION_ASSIGNED' | 'CONVERSATION_TRANSFERRED' | 'new_message';

interface NotificationCenterProps {
  onOpenLeadDetail?: (leadId: string) => void;
  onOpenConversation?: (conversationId: string) => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onOpenLeadDetail, onOpenConversation }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { notifications, unreadCount, loading, markAsRead, clearNotifications } = useNotifications();

  const theme = useTheme();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: any) => {
    console.log('🔔 NotificationCenter - Notification clicked:', notification);
    console.log('🔔 NotificationCenter - Notification type:', notification.type);
    console.log('🔔 NotificationCenter - Has conversationId:', !!notification.conversationId, 'Value:', notification.conversationId);
    console.log('🔔 NotificationCenter - Has leadId:', !!notification.leadId, 'Value:', notification.leadId);
    console.log('🔔 NotificationCenter - onOpenConversation available:', !!onOpenConversation);
    console.log('🔔 NotificationCenter - onOpenLeadDetail available:', !!onOpenLeadDetail);

    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    if (notification.leadId && onOpenLeadDetail) {
      console.log('🔔 Opening lead:', notification.leadId);
      onOpenLeadDetail(notification.leadId);
      handleClose();
    } else if (notification.conversationId && onOpenConversation) {
      console.log('🔔 Opening conversation:', notification.conversationId);
      onOpenConversation(notification.conversationId);
      handleClose();
    } else {
      console.log('🔔 No action taken - missing data or callbacks');
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.isRead);
    await Promise.all(unreadNotifications.map(n => markAsRead(n.id)));
  };

  const handleClearAll = () => {
    clearNotifications();
    handleClose();
  };
  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'LEAD_ASSIGNED':
        return <NewReleases />;
      case 'LEAD_TRANSFERRED':
        return <SwapHoriz />;
      case 'CONVERSATION_ASSIGNED':
        return <Person />;
      case 'CONVERSATION_TRANSFERRED':
        return <SwapHoriz />;
      case 'new_message':
        return <Chat />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case 'LEAD_ASSIGNED':
        return theme.palette.success.main;
      case 'LEAD_TRANSFERRED':
        return theme.palette.warning.main;
      case 'CONVERSATION_ASSIGNED':
        return theme.palette.info.main;
      case 'CONVERSATION_TRANSFERRED':
        return theme.palette.warning.main;
      case 'new_message':
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <>
      <IconButton
        ref={buttonRef}
        color="inherit"
        aria-describedby={id}
        onClick={handleClick}
        sx={{
          ml: 1,
          mr: 1,
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 480,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" component="div">
            Notificações
          </Typography>
          <Box>
            <IconButton
              size="small"
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0}
              title="Marcar todas como lidas"
            >
              <DoneAll fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleClearAll}
              disabled={notifications.length === 0}
              title="Limpar notificações"
              sx={{ ml: 1 }}
            >
              <Clear fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {notifications.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <NotificationsIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
            <Typography variant="body2">Nenhuma notificação</Typography>
          </Box>
        ) : loading ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List sx={{ flexGrow: 1, overflow: 'auto', p: 0 }}>
            {notifications.map((notification) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  disablePadding
                  sx={{
                    bgcolor: notification.isRead
                      ? 'transparent'
                      : alpha(theme.palette.primary.main, 0.04),
                  }}
                >
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: alpha(getNotificationColor(notification.type as NotificationType), 0.1),
                          color: getNotificationColor(notification.type as NotificationType),
                        }}
                      >
                        {getNotificationIcon(notification.type as NotificationType)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="subtitle2"
                            component="span"
                            sx={{
                              fontWeight: notification.isRead ? 400 : 600,
                            }}
                          >
                            {notification.title}
                          </Typography>
                          {!notification.isRead && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'error.main',
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                            sx={{
                              display: 'block',
                              fontWeight: notification.isRead ? 400 : 500,
                            }}
                          >
                            {notification.message}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            {(() => {
                              try {
                                const date = new Date(notification.createdAt);
                                if (isNaN(date.getTime())) {
                                  return 'Data inválida';
                                }
                                return formatDistanceToNow(date, {
                                  addSuffix: true,
                                  locale: ptBR,
                                });
                              } catch (error) {
                                return 'Data inválida';
                              }
                            })()}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>
        )}

        {notifications.length > 0 && (
          <Box
            sx={{
              p: 1,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button
              size="small"
              onClick={handleClose}
              sx={{ textTransform: 'none' }}
            >
              Ver todas as notificações
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default NotificationCenter;


